import { initializeApp, deleteApp } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword, signOut } from 'firebase/auth';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  where,
  limit,
  Unsubscribe
} from 'firebase/firestore';
import { db, auth } from './firebase';
import firebaseConfig from '../../firebase-applet-config.json';
import { UserProfile, UserRole, AppPermission, UserNotificationPreferences } from '../types';
import { getFriendlyAuthErrorMessage } from '../utils/authErrorUtils';
import {
  normalizeRole,
  getDefaultPermissionsForRole,
  ALL_PERMISSIONS,
  CORE_PERMISSION_IDS,
  normalizeNotificationPreferences
} from '../utils/permissions';
import { telemetry } from './telemetryService';

const USERS_COLLECTION = 'users';
const SUBSCRIBERS_COLLECTION = 'volunteer_notification_subscribers';

export async function syncNotificationSubscriber(profile: UserProfile): Promise<void> {
  if (!profile || !profile.uid) return;
  try {
    const cleanEmail = (profile.email || '').trim().toLowerCase();
    if (!cleanEmail) return;

    const subDocRef = doc(db, SUBSCRIBERS_COLLECTION, profile.uid);
    await setDoc(subDocRef, {
      uid: profile.uid,
      email: cleanEmail,
      displayName: profile.displayName || 'Volunteer',
      role: normalizeRole(profile.role),
      rolePresetId: profile.rolePresetId || normalizeRole(profile.role),
      roleTitle: profile.roleTitle || '',
      permissions: Array.isArray(profile.permissions) ? profile.permissions : [],
      notificationPreferences: profile.notificationPreferences || {
        newOrder: true,
        orderUpdates: true,
        newCustomerMessage: true
      },
      status: profile.status || 'active',
      updatedAt: new Date().toISOString()
    }, { merge: true });
  } catch (err) {
    console.warn('[UserService] Failed to sync notification subscriber index:', err);
  }
}

export async function syncAllNotificationSubscribers(users: UserProfile[]): Promise<void> {
  if (!Array.isArray(users) || users.length === 0) return;
  for (const user of users) {
    if (user && user.uid && user.email) {
      syncNotificationSubscriber(user).catch(() => {});
    }
  }
}

async function getAuthHeader(): Promise<Record<string, string>> {
  const currentUser = auth.currentUser;
  if (currentUser) {
    try {
      const token = await currentUser.getIdToken();
      return { Authorization: `Bearer ${token}` };
    } catch (e) {
      // Fallback
    }
  }
  return {};
}

export async function ensureUserProfile(user: {
  uid: string;
  email?: string | null;
  displayName?: string | null;
}): Promise<UserProfile | null> {
  const userDocRef = doc(db, USERS_COLLECTION, user.uid);
  const snap = await getDoc(userDocRef);

  const nowIso = new Date().toISOString();

  if (snap.exists()) {
    const existing = snap.data() as UserProfile;
    const isDesignatedAdmin = user.email && user.email.trim().toLowerCase() === 'mrpostie1979@gmail.com';
    const role: UserRole = isDesignatedAdmin ? 'super_admin' : normalizeRole(existing.role);
    
    let permissions = existing.permissions;
    if (role === 'super_admin') {
      permissions = ALL_PERMISSIONS.map((p) => p.id);
    } else if (!permissions) {
      permissions = [];
    }

    const lastLoginTime = existing.lastLogin ? new Date(existing.lastLogin).getTime() : 0;
    const isLoginRecent = !isNaN(lastLoginTime) && (Date.now() - lastLoginTime < 60 * 60 * 1000);
    const isRoleUnchanged = existing.role === role;
    const isPermissionsUnchanged = Array.isArray(existing.permissions) &&
      existing.permissions.length === permissions.length &&
      existing.permissions.every((p, i) => p === permissions[i]);

    // Only update Firestore document if role/permissions changed or last login was over an hour ago
    if (!isLoginRecent || !isRoleUnchanged || !isPermissionsUnchanged) {
      try {
        await updateDoc(userDocRef, {
          lastLogin: nowIso,
          role,
          permissions
        });
      } catch (e) {
        // non-blocking
      }
      const updatedProfile = { ...existing, role, permissions, lastLogin: nowIso };
      syncNotificationSubscriber(updatedProfile).catch(() => {});
      return updatedProfile;
    }

    return { ...existing, role, permissions };
  }

  // Document does not exist in Firestore!
  console.error(
    `[AUTH ERROR] USER PROFILE NOT FOUND: Firebase Auth account exists (UID: ${user.uid}, Email: ${user.email || 'N/A'}) but Firestore user profile is missing at path users/${user.uid}.`
  );

  // Check if this is the designated super admin or first user bootstrap
  const isDesignatedAdmin = user.email && user.email.trim().toLowerCase() === 'mrpostie1979@gmail.com';
  let isFirstUser = false;
  try {
    const allUsersSnap = await getDocs(query(collection(db, USERS_COLLECTION), limit(1)));
    isFirstUser = allUsersSnap.empty;
  } catch (e) {
    // If listing fails due to permissions, fallback to checking email
  }

  if (isDesignatedAdmin || isFirstUser) {
    console.log(`[AUTH] Auto-bootstrapping Super Admin profile for ${user.email} (UID: ${user.uid})`);
    const role: UserRole = 'super_admin';
    const permissions: AppPermission[] = ALL_PERMISSIONS.map((p) => p.id);
    const newProfile: UserProfile = {
      uid: user.uid,
      email: user.email || 'mrpostie1979@gmail.com',
      displayName: user.displayName || 'Matthew Watson (Super Admin)',
      role,
      rolePresetId: 'super_admin',
      roleTitle: 'Super Administrator',
      permissions,
      notificationPreferences: {
        newOrder: true,
        orderUpdates: true,
        newCustomerMessage: true
      },
      status: 'active',
      createdAt: nowIso,
      lastLogin: nowIso,
      createdBy: 'System (Super Admin Bootstrap)'
    };

    await setDoc(userDocRef, newProfile);
    syncNotificationSubscriber(newProfile).catch(() => {});
    return newProfile;
  }

  // For regular volunteers/users with missing profile documents:
  // Return null so the system displays the "USER PROFILE NOT FOUND" error state
  return null;
}

export async function fetchUserProfile(uid: string): Promise<UserProfile | null> {
  const userDocRef = doc(db, USERS_COLLECTION, uid);
  const snap = await getDoc(userDocRef);
  if (!snap.exists()) {
    console.error(`[AUTH ERROR] USER PROFILE NOT FOUND at path users/${uid}`);
    return null;
  }
  const data = snap.data() as UserProfile;
  return {
    ...data,
    role: normalizeRole(data.role)
  };
}

export async function repairUserProfile(data: {
  uid: string;
  email: string;
  displayName?: string;
  phone?: string;
  role?: UserRole;
  rolePresetId?: string;
  roleTitle?: string;
  permissions?: AppPermission[];
}): Promise<UserProfile> {
  console.log(`[Profile Repair] Repairing missing Firestore document for UID: ${data.uid}, Email: ${data.email}`);
  const nowIso = new Date().toISOString();
  const isDesignated = data.email.trim().toLowerCase() === 'mrpostie1979@gmail.com';
  const selectedRole: UserRole = isDesignated ? 'super_admin' : normalizeRole(data.role || 'standard_user');
  const rolePresetId = selectedRole;
  const permissions: AppPermission[] = selectedRole === 'super_admin'
    ? ALL_PERMISSIONS.map((p) => p.id)
    : (data.permissions || []);

  const profile: UserProfile = {
    uid: data.uid,
    email: data.email.trim().toLowerCase(),
    displayName: (data.displayName || data.email.split('@')[0]).trim(),
    phone: (data.phone || '').trim(),
    role: selectedRole,
    rolePresetId,
    roleTitle: (data.roleTitle || (selectedRole === 'super_admin' ? 'Super Administrator' : 'Standard User')).trim(),
    permissions,
    status: 'active',
    createdAt: nowIso,
    lastLogin: nowIso,
    createdBy: 'System (Profile Repair)'
  };

  const userDocRef = doc(db, USERS_COLLECTION, data.uid);
  await setDoc(userDocRef, profile);
  syncNotificationSubscriber(profile).catch(() => {});

  // Verify write
  const verifySnap = await getDoc(userDocRef);
  if (!verifySnap.exists()) {
    throw new Error(`Profile repair failed: Firestore document users/${data.uid} could not be confirmed.`);
  }

  console.log(`[Profile Repair] Successfully created and verified users/${data.uid}`);
  return profile;
}

export function subscribeUserProfile(
  uid: string,
  onData: (profile: UserProfile | null) => void
): Unsubscribe {
  const userDocRef = doc(db, USERS_COLLECTION, uid);
  return onSnapshot(
    userDocRef,
    (docSnap) => {
      if (docSnap.exists()) {
        const d = docSnap.data() as UserProfile;
        const normalized = {
          ...d,
          role: normalizeRole(d.role)
        };
        onData(normalized);
      } else {
        onData(null);
      }
    },
    (err) => {
      console.error('Error listening to user profile:', err);
    }
  );
}

export function subscribeAllUsers(
  onData: (users: UserProfile[]) => void,
  onError?: (err: Error) => void
): Unsubscribe {
  const usersRef = collection(db, USERS_COLLECTION);
  return onSnapshot(
    usersRef,
    (snapshot) => {
      const usersList: UserProfile[] = snapshot.docs.map((d) => {
        const u = d.data() as UserProfile;
        return {
          ...u,
          role: normalizeRole(u.role)
        };
      });
      usersList.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      onData(usersList);
    },
    (err) => {
      console.warn('Note: Volunteer users subscription encountered restricted or offline state:', err?.message || err);
      onData([]);
      if (onError) onError(err);
    }
  );
}

export async function createVolunteerAccount(data: {
  email: string;
  displayName: string;
  phone?: string;
  role?: UserRole;
  rolePresetId?: string;
  roleTitle?: string;
  permissions?: AppPermission[];
  notificationPreferences?: UserNotificationPreferences;
  createdBy?: string;
}): Promise<UserProfile> {
  const cleanedEmail = (data.email || '').trim().toLowerCase();
  const cleanedName = (data.displayName || '').trim();

  if (!cleanedEmail) {
    throw new Error('Please enter a valid email address.');
  }

  const selectedRole: UserRole = normalizeRole(data.role || 'standard_user');
  const selectedPreset = selectedRole;
  const calculatedPermissions: AppPermission[] = selectedRole === 'super_admin'
    ? ALL_PERMISSIONS.map((p) => p.id)
    : (data.permissions || []);

  console.log(`[Account Creation] Requesting server-side user creation: ${cleanedEmail} (${cleanedName})`);
  console.log(`[Account Creation] Selected role: ${selectedRole}`);

  const authHeaders = await getAuthHeader();
  const res = await fetch('/api/admin/users/create', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders
    },
    body: JSON.stringify({
      email: cleanedEmail,
      displayName: cleanedName,
      phone: data.phone,
      role: selectedRole,
      rolePresetId: selectedPreset,
      roleTitle: data.roleTitle || (selectedRole === 'super_admin' ? 'Super Administrator' : 'Standard User'),
      permissions: calculatedPermissions,
      notificationPreferences: data.notificationPreferences,
      createdBy: data.createdBy || 'Super Admin'
    })
  });

  const resJson = await res.json().catch(() => ({}));
  if (res.ok && resJson.success && resJson.user) {
    const serverUser = resJson.user as UserProfile;
    console.log(`[Account Creation] New Firebase UID: ${serverUser.uid}`);
    telemetry.log({
      level: 'info',
      category: 'auth',
      message: `User account created successfully: ${cleanedEmail} (UID: ${serverUser.uid})`
    });
    return serverUser;
  }

  if (resJson.code === 'EMAIL_EXISTS' || resJson.message?.includes('already registered')) {
    throw new Error('This email address is already registered in Firebase Authentication.');
  }

  throw new Error(resJson.message || 'Failed to create user account on server.');
}

/**
 * Trusted server call to finalize first-time password setup.
 * Clears mustChangePassword and temporaryPasswordExpiresAt, setting passwordChangedAt.
 */
export async function completePasswordSetup(): Promise<{ success: boolean; message: string }> {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('No authenticated user session found.');
  }

  const idToken = await currentUser.getIdToken(true);
  const res = await fetch('/api/auth/complete-password-setup', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`
    }
  });

  const resJson = await res.json().catch(() => ({}));
  if (!res.ok || !resJson.success) {
    throw new Error(resJson.message || 'Failed to complete password setup on server.');
  }

  return {
    success: true,
    message: resJson.message || 'Password setup successfully completed.'
  };
}

export async function updateUserNotificationPreferences(
  uid: string,
  notificationPreferences: Partial<UserProfile['notificationPreferences']>
): Promise<void> {
  const norm = normalizeNotificationPreferences(notificationPreferences);
  const cleanPreferences = {
    newOrders: norm.newOrders,
    dateChangeRequests: norm.dateChangeRequests,
    customerMessages: norm.customerMessages,
    newOrder: norm.newOrders,
    orderUpdates: norm.dateChangeRequests,
    newCustomerMessage: norm.customerMessages
  };

  let serverSuccess = false;
  try {
    const authHeaders = await getAuthHeader();
    const res = await fetch('/api/admin/users/update-preferences', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({
        targetUid: uid,
        notificationPreferences: cleanPreferences
      })
    });
    if (res.ok) {
      serverSuccess = true;
    } else {
      const err = await res.json().catch(() => ({}));
      console.warn('[UserService] /api/admin/users/update-preferences returned error:', err);
    }
  } catch (err) {
    console.warn('[UserService] Server update-preferences failed, attempting client write:', err);
  }

  // Client Firestore synchronization (for local optimistic update or client fallback)
  try {
    const nowIso = new Date().toISOString();
    const userRef = doc(db, USERS_COLLECTION, uid);
    await updateDoc(userRef, {
      notificationPreferences: cleanPreferences,
      updatedAt: nowIso
    });
    const subRef = doc(db, SUBSCRIBERS_COLLECTION, uid);
    await setDoc(subRef, {
      uid,
      notificationPreferences: cleanPreferences,
      updatedAt: nowIso
    }, { merge: true }).catch(() => {});
  } catch (clientErr) {
    if (!serverSuccess) {
      throw clientErr;
    }
  }
}

export async function updateUserRoleAndPermissions(
  uid: string,
  roleData: {
    role?: UserRole;
    rolePresetId?: string;
    roleTitle?: string;
    permissions?: AppPermission[];
  }
): Promise<void> {
  const normalizedRole = roleData.role ? normalizeRole(roleData.role) : undefined;
  const payload: any = {
    targetUid: uid,
    ...(normalizedRole ? { role: normalizedRole, rolePresetId: roleData.rolePresetId || normalizedRole } : {}),
    ...(roleData.roleTitle !== undefined ? { roleTitle: roleData.roleTitle } : {}),
    ...(Array.isArray(roleData.permissions) ? { permissions: roleData.permissions } : {})
  };

  let serverSuccess = false;
  try {
    const authHeaders = await getAuthHeader();
    const res = await fetch('/api/admin/users/update-role', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify(payload)
    });
    if (res.ok) {
      serverSuccess = true;
    } else {
      const err = await res.json().catch(() => ({}));
      console.warn('[UserService] /api/admin/users/update-role returned error:', err);
    }
  } catch (err) {
    console.warn('[UserService] Server update-role failed, attempting client write:', err);
  }

  try {
    const nowIso = new Date().toISOString();
    const clientUpdates: any = {
      ...(normalizedRole ? { role: normalizedRole, rolePresetId: roleData.rolePresetId || normalizedRole } : {}),
      ...(roleData.roleTitle !== undefined ? { roleTitle: roleData.roleTitle } : {}),
      ...(Array.isArray(roleData.permissions) ? { permissions: roleData.permissions } : {}),
      updatedAt: nowIso
    };
    const userRef = doc(db, USERS_COLLECTION, uid);
    await updateDoc(userRef, clientUpdates);
    const subRef = doc(db, SUBSCRIBERS_COLLECTION, uid);
    await setDoc(subRef, clientUpdates, { merge: true }).catch(() => {});
  } catch (clientErr) {
    if (!serverSuccess) {
      throw clientErr;
    }
  }
}

export async function toggleUserStatus(
  uid: string,
  status: 'active' | 'suspended' | 'inactive'
): Promise<void> {
  const normalizedStatus = (status === 'inactive' || status === 'suspended') ? 'suspended' : 'active';
  let serverSuccess = false;
  try {
    const authHeaders = await getAuthHeader();
    const res = await fetch('/api/admin/users/toggle-status', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({
        targetUid: uid,
        status: normalizedStatus
      })
    });
    if (res.ok) {
      serverSuccess = true;
    } else {
      const err = await res.json().catch(() => ({}));
      console.warn('[UserService] /api/admin/users/toggle-status returned error:', err);
    }
  } catch (err) {
    console.warn('[UserService] Server toggle-status failed, attempting client write:', err);
  }

  try {
    const nowIso = new Date().toISOString();
    const userRef = doc(db, USERS_COLLECTION, uid);
    await updateDoc(userRef, { status: normalizedStatus, updatedAt: nowIso });
    const subRef = doc(db, SUBSCRIBERS_COLLECTION, uid);
    await setDoc(subRef, { uid, status: normalizedStatus, updatedAt: nowIso }, { merge: true }).catch(() => {});
  } catch (clientErr) {
    if (!serverSuccess) {
      throw clientErr;
    }
  }
}

export async function updateUserRoleOrStatus(
  uid: string,
  updates: Partial<UserProfile>
): Promise<void> {
  if (!uid || !updates || Object.keys(updates).length === 0) return;

  const promises: Promise<void>[] = [];

  // 1. Notification preferences update (ONLY if explicitly included in updates)
  if (updates.notificationPreferences !== undefined) {
    promises.push(updateUserNotificationPreferences(uid, updates.notificationPreferences));
  }

  // 2. Role or permissions update (ONLY if explicitly included in updates)
  if (updates.role !== undefined || updates.permissions !== undefined || updates.roleTitle !== undefined) {
    promises.push(updateUserRoleAndPermissions(uid, {
      role: updates.role,
      rolePresetId: updates.rolePresetId,
      roleTitle: updates.roleTitle,
      permissions: updates.permissions
    }));
  }

  // 3. Status update (ONLY if explicitly included in updates)
  if (updates.status !== undefined) {
    promises.push(toggleUserStatus(uid, updates.status as any));
  }

  // 4. Other profile fields like displayName or phone
  const otherUpdates: Record<string, any> = {};
  if (updates.displayName !== undefined) otherUpdates.displayName = updates.displayName;
  if (updates.phone !== undefined) otherUpdates.phone = updates.phone;

  if (Object.keys(otherUpdates).length > 0) {
    const nowIso = new Date().toISOString();
    otherUpdates.updatedAt = nowIso;
    const userRef = doc(db, USERS_COLLECTION, uid);
    promises.push(
      updateDoc(userRef, otherUpdates).then(() => {
        const subRef = doc(db, SUBSCRIBERS_COLLECTION, uid);
        return setDoc(subRef, { uid, ...otherUpdates }, { merge: true }).catch(() => {});
      })
    );
  }

  await Promise.all(promises);
}

export async function deleteVolunteerDoc(uid: string): Promise<void> {
  const authHeaders = await getAuthHeader();
  let res: Response;
  try {
    res = await fetch('/api/admin/users/delete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ targetUid: uid })
    });
  } catch (netErr: any) {
    console.error('[UserService] Network failure calling /api/admin/users/delete:', netErr);
    throw new Error('Unable to completely delete this user. No account data was removed. Please try again.');
  }

  const resJson = (await res.json().catch(() => ({}))) as any;
  if (!res.ok || resJson.success !== true) {
    const errorMsg = resJson?.message || 'Unable to completely delete this user. No account data was removed. Please try again.';
    console.error(`[UserService] Server user deletion rejected (HTTP ${res.status}):`, resJson?.code || 'UNKNOWN');
    throw new Error(errorMsg);
  }
}

export async function requestUserPasswordReset(email: string): Promise<void> {
  const res = await fetch('/api/admin/users/reset-password', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email })
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Failed to dispatch password reset email.');
  }
}
